import { useRef, useState } from 'react';
import { useUnmount } from 'react-use';
/**
 * A hook that lets you update a given state after the specified `delay`.
 *
 * @param initialState The initial state.
 * @param delay The delay in ms.
 */
export const useDelayedState = (initialState, delay) => {
    const [state, setState] = useState(initialState);
    const timeoutRef = useRef(null);
    /**
     * Updates the state after the specified `delay`. If another update is in
     * progress, it cancels it and creates a new one. If a `delay` is not
     * specified, it updates the state immediately.
     *
     * @param newState The new state.
     */
    const updateState = (newState) => {
        cancelPending();
        timeoutRef.current = setTimeout(() => {
            setState(newState);
            timeoutRef.current = null;
        }, delay !== null && delay !== void 0 ? delay : 0);
    };
    /** Cancels the ongoing state update, if any. */
    const cancelPending = () => {
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    };
    /** Resets the state to the initial value and cancels any ongoing update. */
    const resetState = () => {
        cancelPending();
        setState(initialState);
    };
    /** Cancels any ongoing update when the component is unmounted. */
    useUnmount(() => {
        cancelPending();
    });
    return [state, updateState, resetState, cancelPending];
};
